import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import PayrollEditor from '../admin/PayrollEditor';
import EmployeePayrollMasterEditor from './EmployeePayrollMasterEditor';
// DEPRECATED: PayrollInitializationWizard has been replaced by validation pipeline in PayrollGeneration tab
// import PayrollInitializationWizard from './PayrollInitializationWizard';
import HistoricalPayrollImport from './HistoricalPayrollImport';
import {
  IndianRupee,
  Calendar,
  Users,
  Play,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  Eye,
  Plus,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  History,
  Loader2,
  Upload,
  Info,
  Search,
  Lock
} from 'lucide-react';
import { buildApiUrl } from '../../utils/apiSetup';
import { formatDate } from '../../utils/dateUtils';

const PayrollManagement = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission?.('payroll:view') || hasPermission?.('*');
  const canGenerate = hasPermission?.('payroll:generate') || hasPermission?.('*');
  const canEditMaster = hasPermission?.('payroll:master:edit') || hasPermission?.('*');
  const canViewMaster = hasPermission?.('payroll:master:view') || canEditMaster || hasPermission?.('*');
  const canHistoricalImport = hasPermission?.('payroll:historical:import') || hasPermission?.('*');
  const canManageAttendance = hasPermission?.('payroll:attendance:manage') || hasPermission?.('*');

  const [searchParams, setSearchParams] = useSearchParams();
  const sectionFromUrl = searchParams.get('section');
  const [activeSection, setActiveSectionState] = useState(() => sectionFromUrl || 'payroll');

  useEffect(() => {
    if (sectionFromUrl && sectionFromUrl !== activeSection) {
      setActiveSectionState(sectionFromUrl);
    }
  }, [sectionFromUrl, activeSection]);

  const setActiveSection = (section, replace = false) => {
    setActiveSectionState(section);
    const nextParams = new URLSearchParams(searchParams);
    if (!section || section === 'payroll') {
      nextParams.delete('section');
    } else {
      nextParams.set('section', section);
    }
    setSearchParams(nextParams, { replace });
  };
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);

  // Pagination and search state for Approved Payrolls
  const [approvedPage, setApprovedPage] = useState(1);
  const [approvedPageSize, setApprovedPageSize] = useState(5);
  const [approvedSearchQuery, setApprovedSearchQuery] = useState('');
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };

  // Historical payroll period selection
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loadingPayrollPeriod, setLoadingPayrollPeriod] = useState(false);
  const [payrollPeriodInfo, setPayrollPeriodInfo] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [workflow, setWorkflow] = useState(null); // 'CURRENT' or 'HISTORICAL'
  const [validating, setValidating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadingAttendance, setUploadingAttendance] = useState(false);
  const [attendanceFile, setAttendanceFile] = useState(null);

  // Attendance Summary Modal states
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceSummaryData, setAttendanceSummaryData] = useState(null);
  const [loadingAttendanceSummary, setLoadingAttendanceSummary] = useState(false);
  const [attendanceSearchQuery, setAttendanceSearchQuery] = useState("");

  useEffect(() => {
    fetchPayrollRuns();
  }, []);

  // Get available months based on selected year
  const getAvailableMonths = () => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    if (selectedYear > currentYear) {
      // Future years: return empty array (disable selection)
      return [];
    } else if (selectedYear === currentYear) {
      // Current year: show months up to current month
      return months.slice(0, currentMonth);
    } else {
      // Past years: show all months
      return months;
    }
  };

  // Reset month when year changes if it's now invalid
  useEffect(() => {
    const availableMonths = getAvailableMonths();
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    if (selectedYear === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
    } else if (availableMonths.length === 0) {
      setSelectedMonth(null);
    }
  }, [selectedYear]);

  const fetchPayrollRuns = async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl('/api/v1/benfish-payroll/runs'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPayrollRuns(data);
      }
    } catch (error) {
      console.error('Error fetching payroll runs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayrollGenerated = async (result) => {
    console.log('handlePayrollGenerated called with:', result);

    // Refresh payroll runs to get the latest data
    await fetchPayrollRuns();

    // Select the newly created payroll run
    if (result && result.payroll_run_id) {
      console.log('Setting selectedRunId to:', result.payroll_run_id);
      setSelectedRunId(result.payroll_run_id);
    }

    // Switch to payroll section
    setActiveSection('payroll');

    console.log('Payroll editor should now open');
  };

  const handleLoadPayrollPeriod = async () => {
    setLoadingPayrollPeriod(true);
    setError('');
    setPayrollPeriodInfo(null);
    setValidationResults(null);

    try {
      const response = await fetch(buildApiUrl('/api/v1/payroll-period/load'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          month: selectedMonth,
          year: selectedYear,
          workflow: "CURRENT"
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPayrollPeriodInfo(data);

        // If payroll exists, open the editor
        if (data.payroll_exists && data.payroll_run_id) {
          setSelectedRunId(data.payroll_run_id);
        } else {
          // Run validation pipeline
          await handleValidatePayrollGeneration();
        }
      } else {
        setError(data.detail || 'Failed to load payroll period');
        setTimeout(() => setError(''), 10000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setTimeout(() => setError(''), 10000);
    } finally {
      setLoadingPayrollPeriod(false);
    }
  };

  const handleValidatePayrollGeneration = async () => {
    setValidating(true);
    setError('');
    setValidationResults(null);

    try {
      const response = await fetch(buildApiUrl('/api/v1/payroll-generation/validate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          payroll_month: selectedMonth,
          payroll_year: selectedYear,
          workflow: "CURRENT"
        })
      });

      const data = await response.json();

      if (response.ok) {
        setValidationResults(data);
        setWorkflow('CURRENT');
      } else {
        setError(data.detail || 'Failed to validate payroll generation');
        setTimeout(() => setError(''), 10000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setTimeout(() => setError(''), 10000);
    } finally {
      setValidating(false);
    }
  };

  const handleGeneratePayroll = async () => {
    // January (1) or July (7) Increment confirmation check
    let incrementConfirmed = false;
    if (selectedMonth === 1 || selectedMonth === 7) {
      const confirmed = window.confirm(
        "Increment Confirmation\n\n" +
        "This is a January/July payroll period.\n\n" +
        "Have you completed all applicable employee increments for this payroll period?"
      );
      if (!confirmed) {
        return; // do not generate payroll
      }
      incrementConfirmed = true;
    }

    setGenerating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(buildApiUrl('/api/v1/payroll-generation/generate'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          payroll_month: selectedMonth,
          payroll_year: selectedYear,
          run_type: 'fulltime',
          affects_loan_lifecycle: true,
          increment_confirmation: incrementConfirmed
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(`Payroll generated successfully! Opening payroll editor...`);
        setSelectedRunId(data.payroll_run_id);
        await fetchPayrollRuns();
      } else {
        setError(data.message || 'Failed to generate payroll');
        setTimeout(() => setError(''), 10000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setTimeout(() => setError(''), 10000);
    } finally {
      setGenerating(false);
    }
  };

  const handleOpenAttendanceSummaryModal = async () => {
    setShowAttendanceModal(true);
    setLoadingAttendanceSummary(true);
    try {
      const response = await fetch(buildApiUrl(`/api/v1/leave/payroll-attendance-summary?month=${selectedMonth}&year=${selectedYear}`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAttendanceSummaryData(data);
      } else {
        setError('Failed to fetch attendance summary');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch attendance summary');
    } finally {
      setLoadingAttendanceSummary(false);
    }
  };

  const handleDownloadAttendanceTemplate = async () => {
    try {
      const response = await fetch(buildApiUrl(`/api/v1/attendance-summary/template/download?month=${selectedMonth}&year=${selectedYear}`),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attendance_summary_template_${selectedMonth}_${selectedYear}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download template');
        setTimeout(() => setError(''), 10000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setTimeout(() => setError(''), 10000);
    }
  };

  const handleUploadAttendanceSummary = async () => {
    if (!attendanceFile) {
      setError('Please select a file to upload');
      setTimeout(() => setError(''), 10000);
      return;
    }

    setUploadingAttendance(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', attendanceFile);

      const response = await fetch(buildApiUrl(`/api/v1/attendance-summary/import/excel?month=${selectedMonth}&year=${selectedYear}`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: formData
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Attendance summary imported successfully! ${data.successful_records} records processed.`);
        setAttendanceFile(null);
        
        // Refresh validation to update the attendance summary check
        await handleValidatePayrollGeneration();
      } else {
        setError(data.detail || 'Failed to import attendance summary');
        setTimeout(() => setError(''), 10000);
      }
    } catch (error) {
      setError('Network error. Please try again.');
      setTimeout(() => setError(''), 10000);
    } finally {
      setUploadingAttendance(false);
    }
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  };

  // Get approved payrolls sorted by date (newest first)
  const approvedPayrolls = useMemo(() => {
    return payrollRuns
      .filter(run => run.status === 'approved')
      .sort((a, b) => {
        const dateA = a.payroll_year * 12 + a.payroll_month;
        const dateB = b.payroll_year * 12 + b.payroll_month;
        return dateB - dateA;
      });
  }, [payrollRuns]);

  // Filter approved payrolls by search term (month name or year)
  const filteredApprovedPayrolls = useMemo(() => {
    if (!approvedSearchQuery.trim()) return approvedPayrolls;
    const term = approvedSearchQuery.toLowerCase().trim();
    return approvedPayrolls.filter(run => {
      const monthName = getMonthName(run.payroll_month)?.toLowerCase() || '';
      const yearStr = String(run.payroll_year || '');
      const combined = `${monthName} ${yearStr}`.toLowerCase();
      return combined.includes(term) || monthName.includes(term) || yearStr.includes(term);
    });
  }, [approvedPayrolls, approvedSearchQuery]);

  const totalApprovedCount = filteredApprovedPayrolls.length;
  const totalApprovedPages = Math.max(1, Math.ceil(totalApprovedCount / approvedPageSize));

  // Safe clamped current page
  const safeApprovedPage = Math.min(Math.max(1, approvedPage), totalApprovedPages);

  const approvedStartIndex = (safeApprovedPage - 1) * approvedPageSize;
  const approvedEndIndex = Math.min(approvedStartIndex + approvedPageSize, totalApprovedCount);
  const paginatedApprovedPayrolls = filteredApprovedPayrolls.slice(approvedStartIndex, approvedEndIndex);

  const handleApprovedSearchChange = (e) => {
    setApprovedSearchQuery(e.target.value);
    setApprovedPage(1); // Reset to page 1 on search
  };

  const handleApprovedPageSizeChange = (e) => {
    setApprovedPageSize(Number(e.target.value));
    setApprovedPage(1); // Reset to page 1 on page-size change
  };

  // Smooth scroll to editor when selectedRunId is activated
  useEffect(() => {
    if (selectedRunId) {
      setTimeout(() => {
        const editorEl = document.getElementById('payroll-editor-section');
        if (editorEl) {
          editorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [selectedRunId]);

  if (!canView) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500 font-medium">
          You do not have permission to view Payroll Management.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex space-x-4 border-b border-gray-200">
        <button
          onClick={() => setActiveSection('payroll')}
          className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center ${activeSection === 'payroll'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
        >
          <IndianRupee className="w-4 h-4 mr-2" />
          Payroll Generation
        </button>
        {canHistoricalImport && (
          <button
            onClick={() => {
              setSelectedRunId(null);
              setActiveSection('historical-import');
            }}
            className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center ${activeSection === 'historical-import'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <Upload className="w-4 h-4 mr-2" />
            Historical Payroll Import
          </button>
        )}
        {canViewMaster && (
          <button
            onClick={() => {
              setSelectedRunId(null);
              setActiveSection('employee-master');
            }}
            className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center ${activeSection === 'employee-master'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Employee Payroll Master
          </button>
        )}
      </div>

      {activeSection === 'employee-master' ? (
        <EmployeePayrollMasterEditor onClose={() => setActiveSection('payroll')} />
      ) : activeSection === 'historical-import' ? (
        <HistoricalPayrollImport
          preselectedMonth={selectedMonth}
          preselectedYear={selectedYear}
          onImportComplete={async () => {
            await fetchPayrollRuns();
          }}
        />
      ) : (
        <>
          {/* Payroll Period Selector */}
          <section className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
              <h2 className="text-xl font-bold text-white flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Payroll Period Selection
              </h2>
            </div>

            <div className="p-6">
              <div className="flex items-end space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-gray-700"
                  >
                    {Array.from({ length: Math.max(1, new Date().getFullYear() - 2026 + 1) }, (_, i) => 2026 + i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    disabled={getAvailableMonths().length === 0}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-100 font-medium text-gray-700"
                  >
                    {getAvailableMonths().length === 0 ? (
                      <option value="">No months available</option>
                    ) : (
                      getAvailableMonths().map((month, index) => (
                        <option key={index + 1} value={index + 1}>{month}</option>
                      ))
                    )}
                  </select>
                </div>
                <button
                  id="load-payroll-btn"
                  onClick={handleLoadPayrollPeriod}
                  disabled={loadingPayrollPeriod}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                >
                  {loadingPayrollPeriod ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      Load Payroll
                    </>
                  )}
                </button>
              </div>

              {/* Notification Banner - Unified with Validation Results */}
              {validationResults && (
                <div className={`mt-4 p-4 rounded-lg border flex items-start ${
                  validationResults.all_valid 
                    ? 'bg-green-50 border-green-200 text-green-800' 
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  {validationResults.all_valid ? (
                    <CheckCircle className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p className="font-semibold text-sm">
                      {validationResults.all_valid 
                        ? 'Payroll is ready to generate.' 
                        : 'Payroll cannot be generated.'}
                    </p>
                    <p className="text-xs mt-1 text-gray-700">
                      {validationResults.all_valid 
                        ? 'All required validations have been completed.' 
                        : 'Please resolve the validation issues shown below before generating payroll.'}
                    </p>
                  </div>
                </div>
              )}

              {/* Validation Results */}
              {validationResults && (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Validation Results</h3>
                    <button
                      onClick={handleValidatePayrollGeneration}
                      disabled={validating}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                    >
                      {validating ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        'Refresh Validations'
                      )}
                    </button>
                  </div>

                  {/* Previous Payroll Check */}
                  <div className={`p-3 rounded-lg border ${validationResults.validations.previous_payroll.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center">
                      {validationResults.validations.previous_payroll.valid ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm text-gray-900">Previous Payroll Check</p>
                        <p className="text-xs text-gray-600">{validationResults.validations.previous_payroll.message}</p>
                      </div>
                    </div>
                    {validationResults.validations.previous_payroll.action_required && (
                      <div className="mt-2 pt-2 border-t flex gap-2">
                        {validationResults.validations.previous_payroll.actions?.map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              if (action.type === 'generate_previous') {
                                setSelectedMonth(validationResults.validations.previous_payroll.previous_month);
                                setSelectedYear(validationResults.validations.previous_payroll.previous_year);
                                handleLoadPayrollPeriod();
                              } else if (action.type === 'import_previous') {
                                setSelectedMonth(validationResults.validations.previous_payroll.previous_month);
                                setSelectedYear(validationResults.validations.previous_payroll.previous_year);
                                setActiveSection('historical-import');
                              }
                            }}
                            className="text-xs px-3 py-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 font-medium"
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                   {/* Attendance Reviews Completion Check */}
                   {validationResults.validations.attendance_reviews && (
                     <div className={`p-3 rounded-lg border ${validationResults.validations.attendance_reviews.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                       <div className="flex items-center justify-between">
                         <div className="flex items-center">
                           {validationResults.validations.attendance_reviews.valid ? (
                             <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                           ) : (
                             <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                           )}
                           <div className="flex-1">
                             <p className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                               Attendance: {validationResults.validations.attendance_reviews.valid ? (
                                 <span className="text-green-700">✓ Complete</span>
                               ) : (
                                 <span className="text-red-700">⚠ {validationResults.validations.attendance_reviews.completed_employees || 0}/{validationResults.validations.attendance_reviews.total_employees || 0} Complete</span>
                               )}
                             </p>
                             <p className="text-xs text-gray-600">{validationResults.validations.attendance_reviews.message}</p>
                           </div>
                         </div>
                         <button
                           type="button"
                           onClick={handleOpenAttendanceSummaryModal}
                           className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                           title="View Attendance Summary"
                         >
                           <Eye className="w-5 h-5" />
                         </button>
                       </div>
                     </div>
                   )}

                  {/* Payroll Readiness Check */}
                  <div className={`p-3 rounded-lg border ${validationResults.validations.payroll_readiness.valid ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center">
                      {validationResults.validations.payroll_readiness.valid ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-sm text-gray-900">Payroll Readiness Check</p>
                        <p className="text-xs text-gray-600">{validationResults.validations.payroll_readiness.message}</p>
                      </div>
                    </div>
                    {validationResults.validations.payroll_readiness.missing_items?.length > 0 && (
                      <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                        {validationResults.validations.payroll_readiness.missing_items.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Generate Payroll Button */}
                  {canGenerate && (
                    <div className="pt-2">
                      <button
                        onClick={handleGeneratePayroll}
                        disabled={!validationResults.can_generate || generating}
                        className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Generating Payroll...
                          </>
                        ) : (
                          <>
                            <Play className="w-5 h-5 mr-2" />
                            Generate Payroll for {getMonthName(selectedMonth)} {selectedYear}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Approved Payrolls History */}
          <section className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center">
                  <History className="w-5 h-5 mr-2" />
                  Approved Payrolls ({approvedPayrolls.length})
                </h2>
                <p className="text-xs text-green-100 mt-0.5">
                  Click a payroll period to open and inspect in the Payroll Editor below
                </p>
              </div>

              {/* Search / Filter input */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search month or year..."
                    value={approvedSearchQuery}
                    onChange={handleApprovedSearchChange}
                    className="pl-9 pr-7 py-1.5 text-sm bg-white text-gray-900 placeholder-gray-500 rounded-lg border border-transparent focus:outline-none focus:ring-2 focus:ring-white shadow-sm w-52 sm:w-60"
                  />
                  {approvedSearchQuery && (
                    <button
                      onClick={() => {
                        setApprovedSearchQuery('');
                        setApprovedPage(1);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm font-bold w-4 h-4 flex items-center justify-center rounded-full hover:bg-gray-100"
                      title="Clear search"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {loading ? (
                <div className="p-8 text-center text-gray-500 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 mr-2 animate-spin text-green-600" />
                  Loading approved payrolls...
                </div>
              ) : approvedPayrolls.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No approved payrolls yet</p>
                </div>
              ) : filteredApprovedPayrolls.length === 0 ? (
                <div className="p-8 text-center">
                  <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No approved payrolls match "{approvedSearchQuery}"</p>
                  <button
                    onClick={() => {
                      setApprovedSearchQuery('');
                      setApprovedPage(1);
                    }}
                    className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium underline"
                  >
                    Clear search filter
                  </button>
                </div>
              ) : (
                paginatedApprovedPayrolls.map(run => {
                  const isSelected = selectedRunId === run.id;
                  return (
                    <div
                      key={run.id}
                      className={`p-4 transition-colors flex items-center justify-between cursor-pointer ${
                        isSelected
                          ? 'bg-green-50/90 border-l-4 border-green-600 shadow-inner'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedRunId(prev => prev === run.id ? null : run.id)}
                    >
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 transition-colors ${
                          isSelected ? 'bg-green-600 text-white shadow-sm' : 'bg-green-100 text-green-600'
                        }`}>
                          <CheckCircle className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">
                              {getMonthName(run.payroll_month)} {run.payroll_year}
                            </h4>
                            {run.is_locked && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 rounded-full flex items-center gap-1" title={run.lock_message || `Locked since ${run.modification_deadline_display}`}>
                                <Lock className="w-3 h-3 text-gray-500" />
                                Locked
                              </span>
                            )}
                            {isSelected && (
                              <span className="px-2 py-0.5 text-xs font-semibold bg-green-200 text-green-800 rounded-full">
                                Opened in Editor
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {run.total_employees} employees • Approved on {formatDate(run.run_date)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <div className="text-right mr-4">
                          <p className="text-xs text-gray-500">Total Net Pay</p>
                          <p className="font-bold text-green-600 text-base">
                            ₹{(run.total_net_pay || 0).toLocaleString('en-IN')}
                          </p>
                        </div>
                        <ChevronRight className={`w-5 h-5 transition-transform duration-200 ${isSelected ? 'text-green-600 rotate-90' : 'text-gray-400'}`} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination Controls */}
            {totalApprovedCount > 0 && (
              <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-700">
                <div className="flex flex-wrap items-center gap-4">
                  <div>
                    Showing <span className="font-semibold text-gray-900">{totalApprovedCount === 0 ? 0 : approvedStartIndex + 1}</span>–<span className="font-semibold text-gray-900">{approvedEndIndex}</span> of <span className="font-semibold text-gray-900">{totalApprovedCount}</span> payrolls
                    {approvedSearchQuery && (
                      <span className="text-xs text-gray-500 ml-1.5">
                        (filtered from {approvedPayrolls.length} total)
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span>Rows per page:</span>
                    <select
                      value={approvedPageSize}
                      onChange={handleApprovedPageSizeChange}
                      className="border border-gray-300 rounded px-2 py-1 text-xs bg-white text-gray-700 focus:ring-1 focus:ring-green-500 focus:outline-none"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-1.5 self-end sm:self-auto">
                  <button
                    onClick={() => setApprovedPage(prev => Math.max(1, prev - 1))}
                    disabled={safeApprovedPage <= 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center transition-colors text-gray-700 shadow-sm"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </button>

                  {/* Page numbers */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalApprovedPages }, (_, i) => i + 1).map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => setApprovedPage(pageNum)}
                        className={`min-w-[32px] h-8 px-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                          pageNum === safeApprovedPage
                            ? 'bg-green-600 text-white font-bold shadow-sm'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => setApprovedPage(prev => Math.min(totalApprovedPages, prev + 1))}
                    disabled={safeApprovedPage >= totalApprovedPages}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs sm:text-sm font-medium bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center transition-colors text-gray-700 shadow-sm"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {/* PayrollEditor Modal */}
      {activeSection === 'payroll' && selectedRunId && (
        <PayrollEditor
          runId={selectedRunId}
          onClose={() => {
            setSelectedRunId(null);
            fetchPayrollRuns();
          }}
        />
      )}

      {/* Attendance Summary Modal */}
      {showAttendanceModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center">
              <h3 className="font-bold text-lg text-white">
                Attendance Summary – {getMonthName(selectedMonth)} {selectedYear}
              </h3>
              <button 
                onClick={() => {
                  setShowAttendanceModal(false);
                  setAttendanceSummaryData(null);
                  setAttendanceSearchQuery("");
                }} 
                className="text-white/80 hover:text-white font-semibold text-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-6 flex-1 flex flex-col overflow-hidden min-h-0">
              {loadingAttendanceSummary ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <>
                  {/* Summary stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                      <p className="text-xs text-blue-600 font-medium uppercase tracking-wider">Total Employees</p>
                      <p className="text-2xl font-bold text-blue-900 mt-1">{attendanceSummaryData?.total_employees || 0}</p>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                      <p className="text-xs text-green-600 font-medium uppercase tracking-wider">Completed</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">{attendanceSummaryData?.completed_employees || 0}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                      <p className="text-xs text-amber-600 font-medium uppercase tracking-wider">Pending</p>
                      <p className="text-2xl font-bold text-amber-900 mt-1">{attendanceSummaryData?.pending_employees || 0}</p>
                    </div>
                  </div>

                  {/* Search Bar */}
                  <div className="relative mb-4">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search employee code or name..."
                      value={attendanceSearchQuery}
                      onChange={(e) => setAttendanceSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Scrollable table */}
                  <div className="overflow-y-auto flex-1 border border-gray-100 rounded-lg">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-gray-700 sticky top-0 uppercase text-[10px] tracking-wider border-b border-gray-150">
                        <tr>
                          <th className="px-4 py-3 bg-gray-50">Employee Code</th>
                          <th className="px-4 py-3 bg-gray-50">Employee Name</th>
                          <th className="px-4 py-3 bg-gray-50 text-center">Working Days</th>
                          <th className="px-4 py-3 bg-gray-50 text-center">Present Days</th>
                          <th className="px-4 py-3 bg-gray-50 text-center">Paid Leave</th>
                          <th className="px-4 py-3 bg-gray-50 text-center">LWP</th>
                          <th className="px-4 py-3 bg-gray-50 text-center">Absent</th>
                          <th className="px-4 py-3 bg-gray-50 text-center">Leave Deduction Days</th>
                          <th className="px-4 py-3 bg-gray-50">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(attendanceSummaryData?.employees?.filter(emp => 
                          emp.employee_code.toLowerCase().includes(attendanceSearchQuery.toLowerCase()) ||
                          emp.employee_name.toLowerCase().includes(attendanceSearchQuery.toLowerCase())
                        ) || []).length === 0 ? (
                          <tr>
                            <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                              No records match your search.
                            </td>
                          </tr>
                        ) : (
                          (attendanceSummaryData?.employees?.filter(emp => 
                            emp.employee_code.toLowerCase().includes(attendanceSearchQuery.toLowerCase()) ||
                            emp.employee_name.toLowerCase().includes(attendanceSearchQuery.toLowerCase())
                          ) || []).map((emp) => (
                            <tr key={emp.employee_id} className="hover:bg-gray-50/50">
                              <td className="px-4 py-3 font-medium text-gray-700">{emp.employee_code}</td>
                              <td className="px-4 py-3 font-semibold text-gray-900">{emp.employee_name}</td>
                              <td className="px-4 py-3 text-center">{emp.working_days}</td>
                              <td className="px-4 py-3 text-center text-green-600 font-medium">{emp.present_days}</td>
                              <td className="px-4 py-3 text-center text-blue-600 font-medium">{emp.paid_leave_days}</td>
                              <td className="px-4 py-3 text-center text-red-500 font-medium">{emp.lwp_days}</td>
                              <td className="px-4 py-3 text-center text-red-600 font-medium">{emp.absent_days}</td>
                              <td className="px-4 py-3 text-center text-red-700 font-bold">{emp.leave_deduction_days}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  emp.attendance_status === 'complete' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                                }`}>
                                  {emp.attendance_status === 'complete' ? 'Complete' : 'Pending'}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollManagement;
